// client/src/components/2 - Components/ProjectsViewTaskView/ProjectsTaskViewTabsDescription.js
import React, { useState, useContext, useEffect, useRef } from "react";
import { Editor, EditorState, ContentState, convertFromHTML, RichUtils, Modifier } from "draft-js";
import { stateToHTML } from "draft-js-export-html";
import { Flex, Text, Button, Box, HStack, IconButton, Tooltip, useColorModeValue } from "@chakra-ui/react";
import {
  PiFloppyDiskBackFill,
  PiPencilSimpleLine,
  PiXBold,
  PiTextBBold,
  PiTextItalicBold,
  PiTextUnderlineBold,
  PiArrowUUpLeftBold,
  PiArrowUUpRightBold,
} from "react-icons/pi";
import { ProjectContext } from "../../5 - General/Context/ProjectsContext";

const styleMap = {
  RED: { color: "red" },
  primary: { color: "primary" },
  GREEN: { color: "green" },
};

const DescriptionSection = ({ isSubtask = false, subtask, initialContent, onSave }) => {
  // -----------------------------
  // 1) Context / State
  // -----------------------------
  const { currentTaskId, tasks, editTask } = useContext(ProjectContext);
  const entity = isSubtask ? subtask : tasks.find((task) => task._id === currentTaskId);

  const [editorState, setEditorState] = useState(() => EditorState.createEmpty());
  const [initialEditorState, setInitialEditorState] = useState(() => EditorState.createEmpty());
  const [isEditing, setIsEditing] = useState(false);
  const editorRef = useRef(null);

  // Use provided initialContent if available; otherwise use entity’s description
  const contentToLoad = initialContent || (entity && entity.description) || "";

  useEffect(() => {
    if (entity && contentToLoad) {
      const blocksFromHTML = convertFromHTML(contentToLoad);
      const contentState = ContentState.createFromBlockArray(blocksFromHTML.contentBlocks, blocksFromHTML.entityMap);
      const newEditorState = EditorState.createWithContent(contentState);
      setEditorState(newEditorState);
      setInitialEditorState(newEditorState);
    } else {
      const emptyState = EditorState.createEmpty();
      setEditorState(emptyState);
      setInitialEditorState(emptyState);
    }
  }, [entity, contentToLoad]);

  // -----------------------------
  // 2) Color / Style variables
  // -----------------------------
  const containerBorderColor = useColorModeValue("gray.200", "gray.600");
  const containerBg = useColorModeValue("gray.50", "gray.800");
  const boxBg = useColorModeValue("white", "gray.700");
  const boxBorderColor = useColorModeValue("gray.200", "gray.600");
  const noDescriptionColor = useColorModeValue("gray.500", "gray.300");

  // -----------------------------
  // 3) Handlers
  // -----------------------------
  const handleSaveDescription = async () => {
    try {
      const htmlDescription = stateToHTML(editorState.getCurrentContent());
      // For subtasks, if an onSave callback is provided, call it
      if (isSubtask && onSave) {
        onSave(htmlDescription);
      } else {
        await editTask(entity._id, { description: htmlDescription });
      }
      setIsEditing(false);
    } catch (error) {
      console.error("Error saving description:", error);
    }
  };

  const handleCancelEdit = () => {
    setEditorState(initialEditorState);
    setIsEditing(false);
  };

  const handleKeyCommand = (command, editorStateParam) => {
    const newState = RichUtils.handleKeyCommand(editorStateParam, command);
    if (newState) {
      setEditorState(newState);
      return "handled";
    }
    return "not-handled";
  };

  const toggleInlineStyle = (style) => {
    const newState = RichUtils.toggleInlineStyle(editorState, style);
    setEditorState(newState);
  };

  const handleUndo = () => {
    const newEditorState = EditorState.undo(editorState);
    setEditorState(newEditorState);
  };

  const handleRedo = () => {
    const newEditorState = EditorState.redo(editorState);
    setEditorState(newEditorState);
  };

  const removeFormatting = () => {
    let newState = editorState;
    const selection = newState.getSelection();
    let contentState = newState.getCurrentContent();

    ["BOLD", "ITALIC", "UNDERLINE", "RED", "primary", "GREEN"].forEach((style) => {
      contentState = Modifier.removeInlineStyle(contentState, selection, style);
    });
    newState = EditorState.push(newState, contentState, "change-inline-style");
    setEditorState(newState);
  };

  // -----------------------------
  // 4) JSX
  // -----------------------------
  return (
    <Flex mx={4} my={8} p={4} borderWidth="1px" borderColor={containerBorderColor} borderRadius="xl" bg={containerBg} flexDir="column">
      <Flex justifyContent="space-between" align="center" mb={2}>
        <Text h="32px" fontWeight="400">
          Task Description
        </Text>
        {isEditing ? (
          <HStack>
            <Button variant="outline" aria-label="Cancel edit" colorScheme="gray" size="xs" onClick={handleCancelEdit}>
              Cancel
            </Button>
            <Button
              w="fit-content"
              leftIcon={<PiFloppyDiskBackFill fontSize="18px" />}
              colorScheme="primary"
              onClick={handleSaveDescription}
              size="xs"
              variant="outline"
            >
              Save Description
            </Button>
          </HStack>
        ) : (
          <Button
            w="fit-content"
            leftIcon={<PiPencilSimpleLine fontSize="18px" />}
            aria-label="Edit description"
            colorScheme="gray"
            size="xs"
            variant="outline"
            onClick={() => setIsEditing(true)}
          >
            Edit
          </Button>
        )}
      </Flex>

      {isEditing ? (
        <Box>
          <HStack spacing={2} mb={2} wrap="wrap">
            <HStack spacing={0}>
              <Tooltip label="Undo">
                <IconButton icon={<PiArrowUUpLeftBold />} aria-label="Undo" onClick={handleUndo} variant="outline" size="sm" />
              </Tooltip>
              <Tooltip label="Redo">
                <IconButton icon={<PiArrowUUpRightBold />} aria-label="Redo" onClick={handleRedo} variant="outline" size="sm" />
              </Tooltip>
            </HStack>

            <HStack spacing={0}>
              {["BOLD", "ITALIC", "UNDERLINE"].map((style) => (
                <Tooltip key={style} label={style}>
                  <IconButton
                    icon={style === "BOLD" ? <PiTextBBold /> : style === "ITALIC" ? <PiTextItalicBold /> : <PiTextUnderlineBold />}
                    aria-label={style}
                    onClick={() => toggleInlineStyle(style)}
                    variant={editorState.getCurrentInlineStyle().has(style) ? "solid" : "outline"}
                    colorScheme={editorState.getCurrentInlineStyle().has(style) ? "primary" : "gray"}
                    size="sm"
                  />
                </Tooltip>
              ))}
            </HStack>

            <Tooltip label="Remove formatting">
              <IconButton icon={<PiXBold />} aria-label="Remove formatting" onClick={removeFormatting} variant="outline" size="sm" />
            </Tooltip>
          </HStack>

          <Box mt={4} bg={boxBg} border="1px solid" borderColor={boxBorderColor} borderRadius="lg" p={2} minH="150px">
            <Editor
              ref={(element) => (editorRef.current = element)}
              editorState={editorState}
              onChange={setEditorState}
              handleKeyCommand={handleKeyCommand}
              customStyleMap={styleMap}
            />
          </Box>
        </Box>
      ) : (
        <Box bg={boxBg} p={4} borderRadius="xl" border="1px solid" borderColor={boxBorderColor} boxShadow="base">
          {entity?.description ? (
            <div dangerouslySetInnerHTML={{ __html: entity.description }} />
          ) : (
            <Text color={noDescriptionColor} fontSize="md">
              No description provided.
            </Text>
          )}
        </Box>
      )}
    </Flex>
  );
};

export default DescriptionSection;
